import { PRACTICE_TYPE_ENUM } from 'data/enums'

export const formatSalary = salary => {
  if (!salary || salary <= 1) return null
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(salary)
}

export const getPracticeTypeString = value => {
  const practiceTypeMap = {
    1: 'FullTime',
    2: 'Locum',
    3: 'Telemedicine',
    4: 'PartTime',
    5: 'PRN',
    6: 'Travel',
  }
  const stringKey = practiceTypeMap[value]

  return PRACTICE_TYPE_ENUM[stringKey] || null
}

/**
 * Generates 3 personalized example queries based on candidate profile.
 * Falls back to generic queries if profile data is missing.
 *
 * @param {Object} candidate - Redux candidate state
 * @returns {Array<string>} Array of exactly 3 query strings
 */
export const generateExampleQueries = (candidate) => {
  const DEFAULT_QUERIES = [
    'Emergency medicine in California',
    'Cardiology with loan forgiveness',
    'Part-time near Boston',
  ]

  if (!candidate) return DEFAULT_QUERIES

  const queries = []

  const getLocationString = (city, state) => {
    const c = city?.trim()
    const s = state?.trim()
    if (c && s) return `near ${c}, ${s}`
    if (c) return `near ${c}`
    if (s) return `in ${s}`
    return null
  }

  const specialty = candidate.specialties?.[0]?.name
  const subspecialty = candidate.subspecialties?.[0]?.name
  const preferredCity = candidate.preferredCity
  const preferredState = candidate.preferredStates?.[0]
  const currentState = candidate.state
  const currentCity = candidate.city
  const practiceTypes = candidate.preferredPracticeTypes || []

  if (specialty && (preferredState || preferredCity)) {
    const location = getLocationString(preferredCity, preferredState)
    queries.push(`${specialty} ${location}`)
  } else if (specialty) {
    queries.push(`${specialty} positions`)
  } else if (preferredState || preferredCity) {
    const location = getLocationString(preferredCity, preferredState)
    queries.push(`Jobs ${location}`)
  } else if (currentState || currentCity) {
    const location = getLocationString(currentCity, currentState)
    queries.push(`Jobs ${location}`)
  } else {
    queries.push(DEFAULT_QUERIES[0])
  }

  if (subspecialty && (preferredCity || currentCity)) {
    const city = preferredCity || currentCity
    queries.push(`${subspecialty} near ${city}`)
  } else if (practiceTypes.length > 0) {
    const practiceType = PRACTICE_TYPE_ENUM[practiceTypes[0]] || practiceTypes[0]
    const location = preferredState || currentState

    if (specialty) {
      queries.push(`${practiceType} ${specialty}`)
    } else if (location) {
      queries.push(`${practiceType} positions in ${location}`)
    } else {
      queries.push(`${practiceType} positions`)
    }
  } else if (specialty) {
    queries.push(`${specialty} with loan forgiveness`)
  } else {
    queries.push(DEFAULT_QUERIES[1])
  }

  const usedLoanForgiveness = queries.some(q => q.includes('loan forgiveness'))

  if (specialty && !usedLoanForgiveness) {
    queries.push(`${specialty} with loan forgiveness`)
  } else if (practiceTypes.length > 1) {
    const practiceType = PRACTICE_TYPE_ENUM[practiceTypes[1]] || practiceTypes[1]
    queries.push(`${practiceType} positions`)
  } else if (currentState && currentState !== preferredState) {
    const location = getLocationString(currentCity, currentState)
    queries.push(`Jobs ${location}`)
  } else {
    queries.push(DEFAULT_QUERIES[2])
  }

  const unique = [...new Set(queries)]

  for (const dq of DEFAULT_QUERIES) {
    if (unique.length >= 3) break
    if (!unique.includes(dq)) unique.push(dq)
  }

  return unique.slice(0, 3)
}

/**
 * Generates contextual follow-up suggestion pills based on the current
 * search results and search arguments.
 *
 * @param {Object} params
 * @param {Array} params.rankedJobs - Current ranked job results
 * @param {Object} params.jobListingArguments - The parsed search criteria
 * @returns {Array<string>} Array of 2-3 suggestion strings
 */
export const generateFollowUpSuggestions = ({ rankedJobs, jobListingArguments }) => {
  const suggestions = []

  const locations = rankedJobs
    ?.map(rj => rj.job?.state || rj.job?.city)
    .filter(Boolean)
  const uniqueLocations = [...new Set(locations)]

  const hospitals = rankedJobs
    ?.map(rj => rj.job?.hospitalName || rj.job?.employer?.name)
    .filter(Boolean)
  const uniqueHospitals = [...new Set(hospitals)]

  const specialty = jobListingArguments?.specialties?.[0]

  // Contextual: location-based
  if (uniqueLocations.length > 0) {
    const location = uniqueLocations[0]
    suggestions.push(`What's the cost of living in ${location}?`)
  }

  // Contextual: hospital-based
  if (uniqueHospitals.length > 0) {
    const hospital = uniqueHospitals[0]
    const shortName = hospital.length > 30 ? hospital.substring(0, 27) + '...' : hospital
    suggestions.push(`Tell me about ${shortName}`)
  }

  // Contextual: school districts
  if (rankedJobs?.length > 0) {
    suggestions.push('Which of these are near good school districts?')
  }

  // Contextual: ask about a specific job
  if (rankedJobs?.length > 0 && suggestions.length < 4) {
    suggestions.push('Ask me about a specific job')
  }

  // Contextual: nature & outdoors
  if (rankedJobs?.length > 0 && suggestions.length < 4) {
    suggestions.push('Which of these are near nature & outdoor activities?')
  }

  // Contextual: specialty job market
  if (specialty && suggestions.length < 4) {
    suggestions.push(`What's the job market like for ${specialty}?`)
  }

  // Contextual: compare locations
  if (uniqueLocations.length > 1 && suggestions.length < 4) {
    suggestions.push('Compare cost of living between these locations')
  }

  // Static fallbacks
  const fallbacks = [
    'Which of these are near good school districts?',
    'Which of these are near nature & outdoor activities?',
    'Jobs with sign-on bonus',
  ]

  for (const fb of fallbacks) {
    if (suggestions.length >= 3) break
    if (!suggestions.includes(fb)) suggestions.push(fb)
  }

  return suggestions.slice(0, 3)
}
